import { PlusOutlined } from "@ant-design/icons";
import { SwatchesIcon } from "@phosphor-icons/react";
import {
  useCustomMutation,
  useInvalidate,
  useList,
  useTranslate,
} from "@refinedev/core";
import { Button, Flex, Form, Modal, Spin, Typography } from "antd";
import { Carousel } from "components/Carousel";
import { FlatBrandKitPreview } from "pages/media/brandkit/components/FlatBrandKitPreview";
import { BaseBrandkitCreate } from "pages/media/brandkit/create";
import { BaseBrandKitEdit } from "pages/media/brandkit/edit";
import { BrandKit, MediaProjectResponse, Transitions } from "pages/media/types";
import { useMemo, useState } from "react";
import { ProjectSettingsFormTransitions } from "./ProjectSettingsFormTransitions";
import { useBrandKitEdit } from "pages/media/brandkit/hooks/useBrandkitEdit/";
import { useAntTheme } from "hooks/useAntTheme/";
const CARD_MAX_WIDTH = 340;
const CAROUSEL_HEIGHT = 360;

export function ProjectSettingsFormBrandKit<T>(
  props: Readonly<{
    project?: MediaProjectResponse;
    value: T | undefined;
    onChange: (value: T) => void;
    onBrandKitChanged?: (brandKit: BrandKit) => void;
    showTransitions?: boolean;
    isWizard?: boolean;
    instanceId?: string;
  }>
) {
  const [brandKitModal, setBrandKitModal] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resource = `media/${props.project?.organization_id}/brand_kits?include_generated=false`;
  const { data, isLoading: isBrandKitsLoading } = useList<BrandKit>({
    resource,
    queryOptions: {
      enabled: !!props.project?.organization_id,
    },
  });
  const t = useTranslate();
  const sortedData = useMemo(
    () => data?.data?.sort((a, b) => a?.name?.localeCompare(b?.name ?? "")),
    [data?.data]
  );

  if (sortedData && sortedData.length > 0 && !props.value) {
    props.onChange(sortedData[0]?.id as T);
  }

  const currentBrandkit = useMemo(
    () => sortedData?.find((bk) => bk.id === props.value) ?? sortedData?.[0],
    [sortedData, props.value]
  );
  const [isNewBrandkit, setIsNewBrandkit] = useState(false);
  const [activeBrandkit, setActiveBrandkit] = useState<BrandKit | undefined>(
    currentBrandkit
  );
  const {
    mutateAsync: setProjectsTransitionType,
    isPending: isLoadingProjectTransitionType,
  } = useCustomMutation();

  const [form] = Form.useForm();
  const { theme } = useAntTheme();
  const {
    handleCreateBrandKit,
    brandKit,
    handleEditSave,
    stagedAssets,
    setStagedAssets,
    optimisticRemoveAsset,
    isBrandKitLoading,
    form: editForm,
    formProps: editFormProps,
    fontRefs,
    videoRefs,
    imageRefs,
    musicRefs,
    setDefaultFonts,
    handleFontSelect,
    fonts,
    updateFontStateOptimistically,
  } = useBrandKitEdit(
    activeBrandkit?.id ?? (props.value as string),
    props.project?.organization_id
  );

  const component = () => {
    if (isTransitioning) {
      return (
        <Flex
          justify="center"
          align="center"
          style={{ width: "100%", height: "100%" }}
        >
          <Spin />
        </Flex>
      );
    }
    if (isNewBrandkit) {
      return (
        <BaseBrandkitCreate
          form={form}
          handleSave={() =>
            handleCreateBrandKit(
              {
                name: form.getFieldsValue().name,
                primaryColor: theme.colorBorder,
              },
              () => {} // Prevent default navigation
            )
          }
          onSuccess={(newBrandKit) => {
            setActiveBrandkit(newBrandKit);
            setIsTransitioning(true);
            props?.onChange(newBrandKit.id as T);
            props?.onBrandKitChanged?.(newBrandKit);
            setTimeout(() => {
              setIsTransitioning(false);
            }, 500);
            setIsNewBrandkit(false);
            setIsSubmitting(false);
          }}
        />
      );
    } else {
      const resolvedBrandKit = brandKit ?? activeBrandkit;
      if (!resolvedBrandKit) {
        return null;
      }
      return (
        <BaseBrandKitEdit
          brandKit={resolvedBrandKit}
          loading={isBrandKitLoading && !activeBrandkit}
          onEditSave={(values) => handleEditSave(values, () => {})} // Prevent default navigation
          onSuccess={() => {
            // We need this invalidation because BK list has different query key
            invalidate({
              resource,
              invalidates: ["list"],
            }).catch((error) => {
              console.error(error);
            });
            setIsSubmitting(false);
            closeBrandKitModal();
          }}
          stagedAssets={stagedAssets}
          setStagedAssets={setStagedAssets}
          uploadRefs={{
            font: fontRefs,
            video: videoRefs,
            image: imageRefs,
            music: musicRefs,
          }}
          optimisticRemoveAsset={optimisticRemoveAsset}
          form={editForm}
          formProps={editFormProps}
          setDefaultFonts={setDefaultFonts}
          handleFontSelect={handleFontSelect}
          fonts={fonts}
          updateFontStateOptimistically={updateFontStateOptimistically}
        />
      );
    }
  };

  const invalidate = useInvalidate();
  const handleTransitionChange = async (value: string) => {
    if (!props.project) return;

    await setProjectsTransitionType({
      url: `media/projects/${props.project.id}/instances/${props.instanceId}/settings`,
      method: "patch",
      values: {
        transitions: value,
      },
    });
    await invalidate({
      resource: "media/projects",
      id: props.project?.id,
      invalidates: ["detail"],
    });
  };

  const closeBrandKitModal = () => {
    if (activeBrandkit) {
      setActiveBrandkit(currentBrandkit); //to reset newBrandkit after saving changes
    }
    setBrandKitModal(false);
  };

  function handleModalOk() {
    if (isSubmitting) return;
    setIsSubmitting(true);

    if (isNewBrandkit) {
      form.submit();
    } else {
      editForm.submit();
    }
  }

  return (
    <>
      <Carousel
        slidesToShow={props.isWizard ? 3 : 2}
        slidesToScroll={props.isWizard ? 3 : 2}
        cardMaxWidth={CARD_MAX_WIDTH}
        style={{ padding: 24 }}
        slideToShowMD={props.isWizard ? 3 : 2}
        slideToShowSM={props.isWizard ? 2 : 1}
        numberOfElements={sortedData?.length ?? 0}
        initialSlide={sortedData?.findIndex((item) => item.id === props.value)}
        loading={isBrandKitsLoading}
        empty={{
          image: <SwatchesIcon size={100} />,
        }}
      >
        {sortedData?.map((item) => {
          return (
            <div
              key={item?.id}
              style={{ flex: 1, maxWidth: `${CARD_MAX_WIDTH}px` }}
            >
              <div
                onClick={() => {
                  props?.onChange(item.id as T);
                  props?.onBrandKitChanged?.(item);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    props?.onChange(item.id as T);
                    props?.onBrandKitChanged?.(item);
                  }
                }}
                role="button"
                tabIndex={0}
                style={{
                  position: "relative",
                  height: CAROUSEL_HEIGHT,
                  margin: "20px 10px",
                }}
              >
                <FlatBrandKitPreview
                  brandKit={item}
                  organizationId={props.project?.organization_id ?? ""}
                  width="100%"
                  onEdit={() => {
                    setActiveBrandkit(item);
                    setBrandKitModal(true);
                  }}
                  deleteButton={false}
                  checked={
                    item.id ===
                    (currentBrandkit ? currentBrandkit?.id : props.value)
                  }
                />
              </div>
            </div>
          );
        })}
      </Carousel>
      <Flex justify="center">
        <Button
          size="large"
          onClick={() => {
            setIsNewBrandkit(true);
            setBrandKitModal(true);
          }}
          shape="round"
          icon={<PlusOutlined />}
          type="default"
        >
          {t("projects.components.ProjectSettingForm.createANewBrandkit")}
        </Button>
      </Flex>

      {/* custom UI to add transition flags (Steve) */}
      {props.showTransitions && (
        <Flex vertical style={{ marginTop: 40 }} gap={10}>
          <Flex vertical>
            <Typography.Text strong>
              {t("projects.components.ProjectSettings.transition")}
            </Typography.Text>
            <Typography.Text type="secondary">
              {t("projects.components.ProjectSettings.chooseTransition")}
            </Typography.Text>
          </Flex>
          <ProjectSettingsFormTransitions<Transitions>
            loading={isLoadingProjectTransitionType}
            onChange={(value) => void handleTransitionChange(value)}
            value={props.project?.settings.transitions ?? "Basic"}
            name={"transitions"}
            valuePropName="checked"
            initialValue={props.project?.settings.transitions ?? "Basic"}
          />
        </Flex>
      )}

      <Modal
        onCancel={() => {
          closeBrandKitModal();
          setIsNewBrandkit(false); //to not open Create if we click on edit after cancel the Modal
        }}
        afterOpenChange={(open) => {
          if (!open) {
            setIsSubmitting(false);
            closeBrandKitModal();
          }
        }}
        okText={t("buttons.next")}
        okButtonProps={{
          disabled: isSubmitting,
          loading: isSubmitting,
          shape: "round",
          size: "large",
        }}
        cancelButtonProps={{
          disabled: isSubmitting,
          shape: "round",
          size: "large",
        }}
        onOk={handleModalOk}
        title={
          isNewBrandkit
            ? t("projects.components.ProjectSettingForm.createBrandkit")
            : t("projects.components.ProjectSettingForm.editBrandkit", {
                brandkitName: activeBrandkit?.name,
              })
        }
        width={"85vw"}
        open={brandKitModal}
        styles={{
          body: {
            minHeight: "60vh",
            paddingTop: 24,
          },
        }}
        style={{
          top: 20,
        }}
        destroyOnHidden //re-render modal inner component after assets are uploaded
      >
        {component()}
      </Modal>
    </>
  );
}
